/**
 * The signed-in user's account state documents served by the forge CMMD
 * proxy: the profile preferences document and the per-namespace
 * settings/keybindings documents that the user settings sync
 * (~/cmmd/userSettingsSync.ts) hydrates and publishes.
 *
 * Extracted from index.ts under the file-size ratchet and bound onto the
 * client in its constructor, the way threadArchive and threadDisposition are.
 *
 * @module environments/cmmdApi/userAccountApi
 */
import type {
  CmmdForgeAccountPreferences,
  CmmdUserSettingsDocument,
  CmmdUserSettingsNamespace,
  PutCmmdUserSettingsParams,
  UpdateCmmdForgeAccountPreferencesParams,
} from "@cmmd-forge/contracts";

import { request, type TransportConfig } from "./transport";

export interface UserAccountApi {
  getForgeAccountPreferences(): Promise<CmmdForgeAccountPreferences>;
  updateForgeAccountPreferences(
    params: UpdateCmmdForgeAccountPreferencesParams,
  ): Promise<CmmdForgeAccountPreferences>;
  getUserSettings(namespace: CmmdUserSettingsNamespace): Promise<CmmdUserSettingsDocument>;
  putUserSettings(
    namespace: CmmdUserSettingsNamespace,
    params: PutCmmdUserSettingsParams,
  ): Promise<CmmdUserSettingsDocument>;
}

export function bindUserAccountApi(cfg: TransportConfig): UserAccountApi {
  return {
    getForgeAccountPreferences: () =>
      request<CmmdForgeAccountPreferences>(
        { method: "GET", path: "/api/cmmd/account/preferences" },
        cfg,
      ),
    updateForgeAccountPreferences: (params) =>
      request<CmmdForgeAccountPreferences>(
        { method: "PATCH", path: "/api/cmmd/account/preferences", body: params },
        cfg,
      ),
    getUserSettings: (namespace) =>
      request<CmmdUserSettingsDocument>(
        { method: "GET", path: `/api/cmmd/user/settings/${namespace}` },
        cfg,
      ),
    putUserSettings: (namespace, params) =>
      request<CmmdUserSettingsDocument>(
        {
          method: "PUT",
          path: `/api/cmmd/user/settings/${namespace}`,
          body: params,
        },
        cfg,
      ),
  };
}
